import { useMemo, useRef } from 'react';
import { useGSAP } from '@gsap/react';
import {
  ComponentRendering,
  Field,
  ImageFieldValue,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import gsap from 'gsap/dist/gsap';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import { sanitize } from 'isomorphic-dompurify';

import * as icons from '@gn-audio/icons';
import { JabraIcon } from '@gn-audio/jss-components';

import { getSharedProperty } from 'common/getters';
import { getSharedDictionary } from 'common/getters/getSharedDictionary';
import { ComponentProps } from 'lib/component-props';
import { useBreakpoint } from 'utils/hooks/useBreakpoint';
type EnterpriseImageSpot = {
  backgroundColor: Field<string>;
  backgroundColorShared: Field<string>;
  icon: Field<keyof typeof icons>;
  iconShared: Field<keyof typeof icons>;
  description: Field<string>;
  descriptionShared: Field<string>;
  mobile: Field<ImageFieldValue>;
  mobileShared: Field<ImageFieldValue>;
  tablet: Field<ImageFieldValue>;
  tabletShared: Field<ImageFieldValue>;
  desktop: Field<ImageFieldValue>;
  desktopShared: Field<ImageFieldValue>;
} & ComponentRendering;
type EnterpriseImageSpotProps = {
  fields: EnterpriseImageSpot;
} & ComponentProps;

gsap.registerPlugin(useGSAP, ScrollTrigger);

const EnterpriseImageSpot = ({ fields }: EnterpriseImageSpotProps) => {
  const boxRef = useRef(null);
  const backgroundColor = getSharedProperty(fields, 'backgroundColor')?.value;
  const iconName = getSharedProperty(fields, 'icon');

  const description = getSharedDictionary(fields, 'description')?.value;
  const { isAboveMd } = useBreakpoint('md', true);
  const { isAboveLg } = useBreakpoint('lg', true);
  const { isAboveXs } = useBreakpoint('xs', true);

  const image = useMemo(() => {
    if (isAboveLg) {
      return getSharedProperty(fields, 'desktop')?.value;
    }
    if (isAboveMd) {
      return getSharedProperty(fields, 'tablet')?.value;
    }
    return getSharedProperty(fields, 'mobile')?.value;
  }, [isAboveMd, isAboveLg, fields]);

  useGSAP(() => {
    const current = boxRef.current;
    gsap.from(current, {
      y: '+=100',
      scale: 1.2,
      ease: 'power1.inOut',
    });
    gsap.to(current, {
      scrollTrigger: {
        trigger: current,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 2,
      },
      scale: 1,
      clipPath: 'circle(100%)',
    });
  }, []);
  return (
    <div
      className="test relative h-screen w-full overflow-hidden"
      style={{ backgroundColor: backgroundColor }}
    >
      <img
        ref={boxRef}
        className="absolute h-screen w-full scale-110 object-cover"
        src={image?.src}
        alt={image?.alt as string}
      />
      {description && (
        <div className="z-1 absolute flex h-full w-full items-end justify-end">
          <div className="m-3 flex w-[420px] items-center justify-start rounded-xl bg-white p-6 xs:m-10">
            {isAboveXs && !!iconName?.value && (
              <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-[#0000000d]">
                <JabraIcon className="h-12 w-12" Icon={icons?.[iconName.value]} />
              </div>
            )}
            <div
              className="pl-8 text-base leading-6"
              dangerouslySetInnerHTML={{ __html: sanitize(description) }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default withDatasourceCheck()(EnterpriseImageSpot);
